import { MatchAccessibility, MatchPlayerRole, MatchPlayerStatus } from '@okletsplay/core';
import { ModalName, Route } from 'app/enums';
import { MatchEntity, UserEntity } from 'app/interfaces/entity';
import { modalsReducers } from 'app/store/modal/slice';
import { ChatBoxContext } from 'modules/chat/providers';
import { toastSuccess } from 'modules/utils';
import { useCallback, useContext, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { CardAction } from 'shared/interfaces';

export const useDefaultUserMenuActions = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { updateRecipientId } = useContext(ChatBoxContext);

  const privateMessageHandler = useCallback(
    (user?: UserEntity | null) => {
      if (user?.chat?.isMuted) {
        toastSuccess(`User "${user?.userName}" is muted!`);
        return;
      }

      if (user?.id) {
        updateRecipientId(user?.id);
      }
    },
    [updateRecipientId]
  );

  const viewProfileHandler = useCallback(
    (user?: UserEntity | null) => {
      if (!user) {
        return;
      }

      dispatch(modalsReducers.closeAllModals());
      navigate(`${Route.Players}/${user.id}`);
    },
    [dispatch, navigate]
  );

  const challengeHandler = useCallback(
    (user: UserEntity) => {
      const data: Partial<MatchEntity> = {
        accessibility: MatchAccessibility.Private,
        players: [
          {
            user: user,
            userId: user.userId,
            role: MatchPlayerRole.Player,
            status: MatchPlayerStatus.Invited,
            matchToken: '',
          },
        ],
      };

      dispatch(modalsReducers.openModal({ name: ModalName.CreateMatch, data }));
    },
    [dispatch]
  );

  const viewProfileAction = useMemo<CardAction<UserEntity>>(() => {
    return {
      label: 'View Profile',
      isDisabled: false,
      handler: viewProfileHandler,
      isAllowedWithoutAuthorization: true,
    };
  }, [viewProfileHandler]);

  const privateMessageActions = useMemo<CardAction<UserEntity>[]>(() => {
    return [
      {
        label: 'Private Message',
        isDisabled: false,
        handler: privateMessageHandler,
      },
    ];
  }, [privateMessageHandler]);

  const challengeActions = useMemo<CardAction<UserEntity>[]>(() => {
    return [
      {
        label: 'Challenge',
        isDisabled: false,
        handler: challengeHandler,
      },
    ];
  }, [challengeHandler]);

  return {
    challengeActions,
    challengeHandler,
    viewProfileAction,
    privateMessageHandler,
    privateMessageActions,
    viewProfileHandler,
  };
};
